import { type ReactElement, useState } from 'react';

interface FundRow {
  label: string;
  percentage: number;
}
import { useNavigate } from 'react-router-dom';
import { presaleApi, type PresaleCreateParams } from '../../lib/presale-api';

type Step = 'details' | 'config' | 'preview';

export function PresaleCreatePage(): ReactElement {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<PresaleCreateParams>({
    tokenName: '',
    tokenSymbol: '',
    description: '',
    pitch: '',
    imageUrl: '',
    twitter: '',
    website: '',
    targetSol: 3,
    minSol: 1,
    maxBuyers: 3,
    minContribution: 0.1,
    maxContribution: 5,
    durationMinutes: 60,
    epochCadence: 'DAILY',
  });

  const [fundRows, setFundRows] = useState<FundRow[]>([
    { label: 'Liquidity', percentage: 80 },
    { label: 'Marketing', percentage: 15 },
    { label: 'Development', percentage: 5 },
  ]);

  const update = (field: keyof PresaleCreateParams, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const presale = await presaleApi.create({ ...form, useOfFunds: fundRows });
      // Auto-publish
      await presaleApi.publish(presale.id);
      navigate(`/presale/${presale.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create presale');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full rounded-lg border border-white/[0.1] bg-white/[0.04] px-4 py-3 text-white placeholder:text-white/20 focus:border-[#c4f70e]/50 focus:outline-none text-sm';
  const labelClass = 'text-xs text-white/40 mb-1 block';

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Create Presale</h1>
        <p className="mt-1 text-sm text-white/40">
          Launch a curated token with creator fee redistribution
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex gap-2">
        {(['details', 'config', 'preview'] as Step[]).map((s, i) => (
          <button
            key={s}
            onClick={() => setStep(s)}
            className={`flex-1 rounded-lg py-2 text-xs font-medium transition-colors ${step === s ? 'border border-[#c4f70e]/30 bg-[#c4f70e]/10 text-[#c4f70e]' : 'border border-white/[0.06] bg-white/[0.02] text-white/30'}`}
          >
            {i + 1}. {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Step 1: Token Details */}
      {step === 'details' && (
        <div className="space-y-4 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
          <div>
            <label className={labelClass}>Token Name *</label>
            <input
              value={form.tokenName}
              onChange={(e) => update('tokenName', e.target.value)}
              placeholder="e.g. Pizza Cat"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Token Symbol *</label>
            <input
              value={form.tokenSymbol}
              onChange={(e) => update('tokenSymbol', e.target.value.toUpperCase())}
              placeholder="e.g. PCAT"
              maxLength={8}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="What's the story behind this token?"
              rows={3}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>The Pitch (Story)</label>
            <textarea
              value={form.pitch || ''}
              onChange={(e) => update('pitch', e.target.value)}
              placeholder="Tell the story behind this token. What's the narrative? Why now? What makes it special?"
              rows={6}
              className={inputClass}
            />
            <p className="mt-1 text-[10px] text-white/20">
              This appears as the main pitch section on the presale page.
            </p>
          </div>
          <div>
            <label className={labelClass}>Image URL</label>
            <input
              value={form.imageUrl}
              onChange={(e) => update('imageUrl', e.target.value)}
              placeholder="https://..."
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Twitter</label>
              <input
                value={form.twitter}
                onChange={(e) => update('twitter', e.target.value)}
                placeholder="@handle"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Website</label>
              <input
                value={form.website}
                onChange={(e) => update('website', e.target.value)}
                placeholder="https://..."
                className={inputClass}
              />
            </div>
          </div>
          <button
            onClick={() => setStep('config')}
            disabled={!form.tokenName || !form.tokenSymbol}
            className="w-full rounded-lg bg-[#c4f70e] px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#c4f70e]/90 disabled:opacity-50"
          >
            Next: Configure Presale →
          </button>
        </div>
      )}

      {/* Step 2: Presale Config */}
      {step === 'config' && (
        <div className="space-y-4 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Target SOL *</label>
              <input
                type="number"
                step="0.1"
                value={form.targetSol}
                onChange={(e) => update('targetSol', parseFloat(e.target.value))}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Minimum SOL</label>
              <input
                type="number"
                step="0.1"
                value={form.minSol}
                onChange={(e) => update('minSol', parseFloat(e.target.value))}
                className={inputClass}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Max Buyers</label>
              <input
                type="number"
                min={1}
                max={3}
                value={form.maxBuyers}
                onChange={(e) => update('maxBuyers', parseInt(e.target.value))}
                className={inputClass}
              />
              <p className="mt-1 text-[10px] text-white/20">Max 3 (Block-0 limit)</p>
            </div>
            <div>
              <label className={labelClass}>Min Contribution (SOL)</label>
              <input
                type="number"
                step="0.01"
                value={form.minContribution}
                onChange={(e) => update('minContribution', parseFloat(e.target.value))}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Max Contribution (SOL)</label>
              <input
                type="number"
                step="0.1"
                value={form.maxContribution}
                onChange={(e) => update('maxContribution', parseFloat(e.target.value))}
                className={inputClass}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Duration (minutes)</label>
              <input
                type="number"
                min={5}
                value={form.durationMinutes}
                onChange={(e) => update('durationMinutes', parseInt(e.target.value))}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Reward Epoch</label>
              <select
                value={form.epochCadence}
                onChange={(e) => update('epochCadence', e.target.value)}
                className={inputClass}
              >
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
              </select>
            </div>
          </div>
          {/* Use of Funds Builder */}
          <div>
            <label className={labelClass}>Use of Funds</label>
            <div className="space-y-2">
              {fundRows.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={row.label}
                    onChange={(e) => {
                      const updated = fundRows.map((r, idx) =>
                        idx === i ? { ...r, label: e.target.value } : r
                      );
                      setFundRows(updated);
                    }}
                    placeholder="Category"
                    className={`${inputClass} flex-1`}
                  />
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={row.percentage}
                    onChange={(e) => {
                      const updated = fundRows.map((r, idx) =>
                        idx === i ? { ...r, percentage: parseInt(e.target.value) || 0 } : r
                      );
                      setFundRows(updated);
                    }}
                    className={`${inputClass} w-24`}
                  />
                  <span className="text-sm text-white/40">%</span>
                  <button
                    type="button"
                    onClick={() => setFundRows(fundRows.filter((_, idx) => idx !== i))}
                    className="px-2 text-lg leading-none text-white/30 transition-colors hover:text-red-400"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setFundRows([...fundRows, { label: '', percentage: 0 }])}
              className="mt-2 text-xs text-[#c4f70e]/60 transition-colors hover:text-[#c4f70e]"
            >
              + Add row
            </button>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStep('details')}
              className="flex-1 rounded-lg border border-white/[0.1] px-4 py-3 text-sm text-white/60 transition-colors hover:bg-white/[0.04]"
            >
              ← Back
            </button>
            <button
              onClick={() => setStep('preview')}
              className="flex-1 rounded-lg bg-[#c4f70e] px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#c4f70e]/90"
            >
              Next: Preview →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Preview & Launch */}
      {step === 'preview' && (
        <div className="space-y-4 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
          <h3 className="text-lg font-semibold text-white">Review Your Presale</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ['Token', `${form.tokenName} ($${form.tokenSymbol})`],
              ['Target', `${form.targetSol} SOL`],
              ['Min Raise', `${form.minSol} SOL`],
              ['Max Buyers', String(form.maxBuyers)],
              ['Contribution', `${form.minContribution} - ${form.maxContribution} SOL`],
              ['Duration', `${form.durationMinutes} minutes`],
              ['Reward Epoch', form.epochCadence || 'DAILY'],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between border-b border-white/[0.04] py-2">
                <span className="text-white/40">{label}</span>
                <span className="font-mono text-white">{value}</span>
              </div>
            ))}
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div className="flex gap-3">
            <button
              onClick={() => setStep('config')}
              className="flex-1 rounded-lg border border-white/[0.1] px-4 py-3 text-sm text-white/60 transition-colors hover:bg-white/[0.04]"
            >
              ← Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 rounded-lg bg-[#c4f70e] px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#c4f70e]/90 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : '🚀 Create & Publish'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
